import { Modal, Typography, Card, Box, Grid, Button, Divider, CircularProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import NumberFormat from 'react-number-format';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { getExportApplicationDetails, selectApplicationsState } from '../sagas/pm/applicationsSlice';
import { ApplicationFileTypeEnum } from '@inosio/payments-sdk';
import { selectCurrentUser } from '../../../shared/sagas/auth/authSlice';
import ExportIcon from '../../../icons/exportIcon2.svg';
import { getExportTransDetails } from '../../../pmui/transactions/sagas/pmTransactionsSlice';

const useStyles = makeStyles({
  modalContainer: {
    background: '#FFFFFF',
    boxShadow: 'none',
    borderRadius: '24px',
  },
  cardSytle: {
    position: 'relative' as 'relative',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '16px',
    pointerEvents: 'auto',
    width: '98%',
    maxWidth: '472px',
    minHeight: '523px',
    borderRadius: '24px',
    outline: 'none',
  },
  titleText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    fontSize: '18px !important',
    lineHeight: '22px !important',
    color: '#000000 !important',
    textAlign: 'center',
  },
  reviewAndSubmitMainDetails: {
    marginBottom: '18px',
  },
  downloadPdfText: {
    fontFamily: 'Open Sans !important',
    fontSize: '14px !important',
    lineHeight: '18px !important',
    color: '#39A2DB !important',
  },
});

interface ApplicationPaymentSuccessModalProps {
  open: boolean;
  appUuid: string;
  applicantUuid: string;
  paymentID: string;
  propertyName: string;
  companyName: string;
  applicantName: string;
  paidAmount: number;
  totalAmount: number;
  transactionDate: string;
  paymentFees: number;
  last4: string;
  closeCallback: () => void;
}

export default function ApplicationPaymentSuccessModal(props: ApplicationPaymentSuccessModalProps) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const currentUser = useSelector(selectCurrentUser);
  const { isExportingApplicationDetails } = useSelector(selectApplicationsState);

  const downloadAppFiles = () => {
    dispatch({
      type: getExportApplicationDetails.type,
      payload: {
        AppUuid: props.appUuid,
        DesiredFileDto: {
          Type: ApplicationFileTypeEnum.ApplicationDetail,
          ApplicantUuid: currentUser?.currentProfile.uuid,
        },
      },
    });
  };

  return (
    <div className={`${classes.modalContainer}`}>
      <Modal
        open={props.open}
        onClose={() => props.closeCallback()}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        BackdropProps={{
          invisible: false,
        }}
      >
        <Card className={classes.cardSytle}>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            <Typography className={classes.titleText} sx={{ mt: 1 }}>
              Your application has been submitted.
            </Typography>
          </Box>

          <Typography
            style={{
              color: '#14142B',
              fontSize: '14px',
              textAlign: 'center',
            }}
            sx={{ mt: 2 }}
            id="review-submit-main-details"
          >
            Your payment is credited to your account immediately. However, it may take up to three business days to reflect on your card or bank statement.
          </Typography>

          <Typography id="payment-id" sx={{ mt: 1 }}>
            Payment ID: {props.paymentID}
          </Typography>
          <Typography id="payment-details-date">{format(new Date(props.transactionDate), 'EEEE, MMM d yyyy hh:mm a')}</Typography>

          <Box sx={{ width: '100%', mt: 3, mb: 8 }}>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} position={'relative'}>
              <Grid item xs={6}>
                <div>
                  <Typography
                    style={{
                      color: '#14142B',
                      fontSize: '16px',
                    }}
                    id="review-submit-main-details"
                  >
                    {props.companyName.length > 20 ? `${props.companyName.slice(0, 20)}...` : props.companyName}
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={6}>
                <div>
                  <Typography
                    style={{
                      color: '#14142B',
                      fontSize: '16px',
                    }}
                    id="review-submit-main-details"
                  >
                    {props.propertyName.length > 20 ? `${props.propertyName.slice(0, 20)}...` : props.propertyName}
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={6}>
                <div className={classes.reviewAndSubmitMainDetails}>
                  <Typography
                    style={{
                      color: '#14142B',
                      fontSize: '16px',
                    }}
                    id="review-submit-main-details"
                  >
                    {props.applicantName.length > 20 ? `${props.applicantName.slice(0, 20)}...` : props.applicantName}
                  </Typography>
                </div>
              </Grid>
            </Grid>
            <Divider sx={{ mx: '-16px' }} />
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} sx={{ marginTop: '8px' }}>
              <Grid item xs={8}>
                <div>
                  <Typography id="review-submit-bank-details-text">Payment Method</Typography>
                </div>
              </Grid>
              <Grid item xs={4}>
                <div>
                  <Typography id="review-submit-sub-bank-details" sx={{ fontWeight: 400 }}>
                    {props.last4}
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={8}>
                <div>
                  <Typography id="review-submit-sub-details">Amount</Typography>
                </div>
              </Grid>
              <Grid item xs={4}>
                <div>
                  <Typography id="review-submit-sub-details" sx={{ fontWeight: 400 }}>
                    <NumberFormat
                      thousandsGroupStyle="thousand"
                      value={props.paidAmount / 100}
                      prefix="$"
                      decimalSeparator="."
                      displayType="text"
                      type="text"
                      thousandSeparator={true}
                      allowNegative={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={8}>
                <div>
                  <Typography id="review-submit-sub-details">Fee Amount</Typography>
                </div>
              </Grid>
              <Grid item xs={4}>
                <div>
                  <Typography id="review-submit-sub-details" sx={{ fontWeight: 400 }}>
                    <NumberFormat
                      thousandsGroupStyle="thousand"
                      value={props.paymentFees / 100}
                      prefix="$"
                      decimalSeparator="."
                      displayType="text"
                      type="text"
                      thousandSeparator={true}
                      allowNegative={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={8}>
                <div>
                  <Typography id="review-submit-sub-details">Total Amount</Typography>
                </div>
              </Grid>
              <Grid item xs={4}>
                <div>
                  <Typography id="review-submit-sub-details" sx={{ fontWeight: 400 }}>
                    <NumberFormat
                      thousandsGroupStyle="thousand"
                      value={props.totalAmount / 100}
                      prefix="$"
                      decimalSeparator="."
                      displayType="text"
                      type="text"
                      thousandSeparator={true}
                      allowNegative={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={8}>
                <div>
                  <Typography id="review-submit-sub-details">Transaction Date</Typography>
                </div>
              </Grid>
              <Grid item xs={4}>
                <div>
                  <Typography id="review-submit-sub-details" sx={{ fontWeight: 400 }}>
                    {format(new Date(props.transactionDate), 'MM/dd/yy')}
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={12}>
                <img
                  src={ExportIcon}
                  alt="close"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    dispatch({
                      type: getExportTransDetails.type,
                      payload: props.paymentID,
                    });
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', position: 'absolute', bottom: 12, width: '100%', pr: 4 }}>
            {isExportingApplicationDetails ? (
              <Box sx={{ width: '200px', display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <Button
                variant="outlined"
                sx={{
                  border: '2px solid #39A2DB !important',
                  borderRadius: '24px',
                  padding: '2px 10px',
                }}
                onClick={downloadAppFiles}
              >
                <Typography sx={{ textTransform: 'capitalize' }} className={classes.downloadPdfText}>
                  Download Full Application
                </Typography>
              </Button>
            )}

            <Button
              sx={{
                '&.Mui-disabled': {
                  color: '#fff',
                },
                width: 99,
              }}
              onClick={props.closeCallback}
              variant="contained"
              id="payment-modal-btn"
            >
              Done
            </Button>
          </Box>
        </Card>
      </Modal>
    </div>
  );
}
